import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';
import {
  OktaUserRequest,
  OktaUserService,
} from '../services/okta-user';
import { ContactService } from 'src/app/core/services/contact';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-onboarding-okta-user',
  templateUrl: 'onboarding-okta-user.component.html',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    FormsModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingOktaUserComponent implements OnInit, OnDestroy {
  onboardingForm: FormGroup;

  contactErrorMessage: string | null = null;

  successMessage: string | null = null;

  isSubmitting: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService,
    private oktaUserService: OktaUserService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.onboardingForm = this.fb.group({
      email: ['', [Validators.email]],
      accountNumber: [''],
      username: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    this.contactErrorMessage = null;
    this.successMessage = null;

    if (this.onboardingForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;

    const { email, username } = this.onboardingForm.value;

    this.contactService
      .getContactByOktaUsernameAndEmail(email, username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contact) => {
          if (!contact) {
            this.contactErrorMessage = `No contact found for ${email} / ${username}.`;
            this.cdr.markForCheck();
            return;
          }

          const payload: OktaUserRequest = {
            email: contact.Email,
            login: contact.OktaUsername__c,
            firstName: contact.FirstName,
            lastName: contact.LastName,
            accountId: contact.AccountId,
          };

          this.oktaUserService
            .onboardUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.successMessage =
                  'User successfully created. An activation email should be received shortly.';
                this.isSubmitting = false;
                this.cdr.markForCheck();
              },
              error: (error) => {
                this.isSubmitting = false;
                this.contactErrorMessage = `Failed to create user. ${error?.message}`;
                this.cdr.markForCheck();
                console.error('Failed to create user:', error);
              },
            });
        },
        error: (error) => {
          this.isSubmitting = false;
          this.contactErrorMessage =
            (error?.message as string) ||
            'An error occurred looking up the contact.';
          this.cdr.markForCheck();
          console.error('Contact lookup error:', error);
        },
      });
  }
}
